import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import {
	ArrowButton,
	contentWrapBreakpoint,
	Css,
	H2,
	H3,
	Paper,
} from '../../../../ui';
import {
	getOverlapWithHeaderStyles,
	StyledHeader,
} from '../../../shared/components';
import ContentWrap from '../../../../ui/components/Layout/ContentWrap';
import themed from '../../../../ui/themer/themed';
import Responsive from '../../../../ui/utils/Responsive';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import vehicleIcon from '../../../../../assets/vehicle-with-blob.svg?reference';

interface IEmptyStateSectionProps {
	title?: string;
	onAddVehicle?: () => void;
	addVehicleButtonText?: string;
	children?: ReactNode;
}

export const VehiclesEmptyStateContainer = observer(
	({ onAddVehicle, addVehicleButtonText }: IEmptyStateSectionProps) => {
		const { t } = useTranslation();
		return (
			<StyledContentWrap backgroundTheme="purple-blue">
				<ContentWrap>
					<StyledPaper>
						<StyledImage src={vehicleIcon} alt="" />
						<Section title={t('account_vehicles_empty_state_header')}>
							<Container>
								<StyledText>
									{t('account_vehicles_empty_state_add_vehicle_explainer')}
								</StyledText>
								<AddVehicleButton type="submit" onClick={onAddVehicle}>
									{addVehicleButtonText}
								</AddVehicleButton>
							</Container>
						</Section>
					</StyledPaper>
				</ContentWrap>
			</StyledContentWrap>
		);
	},
);

const Section = ({
	title,
	children,
}: {
	title?: string;
	children?: ReactNode;
}) => (
	<>
		{title && <StyledVehiclesHeading>{title}</StyledVehiclesHeading>}
		<DesktopSpace />
		{children}
	</>
);

export const StyledContentWrap = styled.div`
	width: 100%;
	margin: 0 auto;

	${StyledHeader} + style + &,
		${StyledHeader} + & {
		${getOverlapWithHeaderStyles}
	}

	@media (${contentWrapBreakpoint}) {
		max-width: none;
		margin: 0 ${themed(({ layout }) => layout.main.margin.tablet)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-left: 0;
	}
`;

const StyledPaper = styled(Paper)`
	max-width: ${Css.pxToRem(611)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: flex;
		flex-direction: column;
		align-items: center;

		max-width: none;
		padding: 24px;
		gap: 10px;
	}
`;

const StyledImage = styled.img`
	margin: -20px 0 -10px -10px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: ${Css.pxToRem(145)};
		height: ${Css.pxToRem(130)};
	}
`;

export const StyledVehiclesHeading = styled(H3.withComponent(H2))`
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin: 0;
	}
`;

const Container = styled.div`
	display: flex;
	align-items: center;
	margin-top: 20px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: block;
		margin-top: 0;
		text-align: center;
	}
`;

const DesktopSpace = styled.hr`
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: none;
	}
`;

const StyledText = styled.p`
	display: flex;
	flex: 1;
	margin: 0;

	line-height: 22px;
	white-space: pre-line; // forces line break

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(14)};
	}
`;

const AddVehicleButton = styled(ArrowButton)`
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(16)};
		margin-top: 20px;
	}
`;
